import { useRef, useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Camera, Trash2, ExternalLink, Info } from "lucide-react";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { PublicAdoptionListingStatusBadge } from "@/components/animals/PublicAdoptionListingStatusBadge";
import { AnimalChecklistProgress } from "@/components/animals/AnimalChecklistProgress";
import { AdoptionListingChecklistItem, ChecklistItemDisplay } from "@/components/animals/AdoptionListingChecklistItem";
import { EntityNotesSection } from "@/components/notes/entity-notes-section";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth-context";

function formatInET(dateStr: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  }).format(new Date(dateStr));
}

export interface AnimalProfileData {
  profileStatus: "unpublished" | "ready" | "published" | "archived";
  lastScrapedAt: string | null;
  bioText: string | null;
  profileFound: boolean;
  photos: Array<{
    id: string;
    s3KeyFull: string;
    s3KeyThumb: string;
    signedUrlFull: string | null;
    signedUrlThumb: string | null;
    isPrimary: boolean;
  }>;
  checklistState: ChecklistItemDisplay[];
}

interface AnimalProfileTabProps {
  animalId: string;
  animalName: string;
  animalSpecies: string;
}

const compressImage = (file: File, maxDimension = 1024, quality = 0.85): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality,
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export function AnimalProfileTab({ animalId, animalName, animalSpecies }: AnimalProfileTabProps) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  const photoInputRef = useRef<HTMLInputElement>(null);

  const [bioExpanded, setBioExpanded] = useState(false);
  const [bioOverflows, setBioOverflows] = useState(false);
  const bioContentRef = useRef<HTMLDivElement>(null);

  const { data: profileData, isLoading } = useQuery<AnimalProfileData>({
    queryKey: ["/api/animals", animalId, "profile"],
    enabled: !!animalId,
  });

  const { data: animalData } = useQuery<{ photoUrl: string | null }>({
    queryKey: [`/api/animals/${animalId}`],
    enabled: !!animalId,
  });

  const possessiveName = animalName.endsWith("s") ? `${animalName}'` : `${animalName}'s`;
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  useEffect(() => {
    const el = bioContentRef.current;
    if (!el) return;
    setBioOverflows(el.scrollHeight > el.clientHeight);
  }, [profileData?.bioText, bioExpanded]);

  const photoUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const csrfToken = document.cookie
        .split("; ")
        .find((c) => c.startsWith("XSRF-TOKEN="))
        ?.split("=")[1];
      const response = await fetch(`/api/animals/${animalId}/photo`, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: csrfToken ? { "X-XSRF-TOKEN": csrfToken } : {},
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(err.message);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/animals/${animalId}`] });
      toast({ title: "Photo updated", description: "Animal photo has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    },
  });

  const photoRemoveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/animals/${animalId}/photo`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/animals/${animalId}`] });
      toast({ title: "Photo removed", description: "Animal photo has been removed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove photo", variant: "destructive" });
    },
  });

  const toggleChecklistMutation = useMutation({
    mutationFn: async ({ definitionId, isComplete }: { definitionId: string; isComplete: boolean }) => {
      await apiRequest("PATCH", `/api/animals/${animalId}/profile-checklist/${definitionId}`, { isComplete });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "profile"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Could not update checklist item.", variant: "destructive" });
    },
  });

  const handlePhotoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Invalid file type", description: "Please upload a JPEG, PNG, or WebP image.", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 10MB.", variant: "destructive" });
      return;
    }
    try {
      const compressed = await compressImage(file);
      photoUploadMutation.mutate(compressed);
    } catch {
      photoUploadMutation.mutate(file);
    }
    e.target.value = "";
  };

  const scrapedPhotoUrl = profileData?.photos?.[0]?.signedUrlFull ?? null;
  const listingUrl = `https://www.mspca.org/pets/${animalId.toLowerCase()}/`;
  const lastCheckedLabel = profileData?.lastScrapedAt
    ? formatInET(profileData.lastScrapedAt)
    : "Never";

  return (
    <div className="space-y-6" data-testid="content-animal-profile-tab">

      {/* Row 1: Checklist + Notes */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 items-stretch">

      {/* 1. Adoption Readiness section */}
      <Card>
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-1.5">
            <CardTitle>Adoption Readiness</CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="About adoption readiness"
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[260px]">
                <p className="text-xs leading-relaxed">
                  Keep track of what needs to be completed before {possessiveName} adoption listing goes live.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          {profileData && profileData.checklistState.length > 0 && (
            <AnimalChecklistProgress
              checklistType="readiness"
              done={profileData.checklistState.filter((i) => i.isComplete).length}
              total={profileData.checklistState.length}
            />
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : profileData && profileData.checklistState.length > 0 ? (
            <>
              <div className="space-y-1 list-zebra">
                {[...profileData.checklistState]
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((item) => (
                    <AdoptionListingChecklistItem
                      key={item.definitionId}
                      item={item}
                      onToggle={(definitionId, isComplete) =>
                        toggleChecklistMutation.mutate({ definitionId, isComplete })
                      }
                      isLoading={toggleChecklistMutation.isPending}
                    />
                  ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="text-checklist-empty">
              No checklist items have been configured yet. Visit Settings &rarr; Animal Profiles to
              add items.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 3. Notes section */}
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center gap-1.5">
            <CardTitle>Adoption Profile Notes</CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="About adoption profile notes"
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[260px]">
                <p className="text-xs leading-relaxed">
                  A space to collaborate on {animalName}&apos;s adoption profile or use as a scratch pad for notes that aren&apos;t ready for Chameleon.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto min-h-0">
          <EntityNotesSection
            entityType="animal_profile"
            entityId={animalId}
            canEdit={!!currentUser}
            currentUserId={currentUser?.id}
            hideLabel
            onExpand={() => setNotesModalOpen(true)}
            shareLabel={`${animalName} (${animalId}) — Profile Notes`}
          />
        </CardContent>
      </Card>
      </div>

      {/* 2. Adoption Profile section */}
      <Card>
        <CardHeader className="pb-5">
          {/* On mobile: title+button above, status+timestamp below.
              On md+: title+button left, status+timestamp right on the same row. */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            {/* Left: title + view button (published only) */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <CardTitle>Adoption Profile</CardTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="About adoption profile"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[260px]">
                    <p className="text-xs leading-relaxed">
                      BETI scans the MSPCA website daily to retrieve the latest version of {possessiveName} public adoption listing.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {!isLoading && profileData?.profileStatus === "published" && (
                <Button variant="outline" size="sm" asChild data-testid="button-view-live-profile">
                  <a href={listingUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1.5" />
                    View Live Profile
                  </a>
                </Button>
              )}
            </div>

            {/* Right: status + last scanned */}
            {isLoading ? (
              <div className="flex gap-3">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-3 w-36 self-center" />
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Status:</span>
                  {profileData && (
                    <PublicAdoptionListingStatusBadge status={profileData.profileStatus} />
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="About adoption profile status"
                        data-testid="button-profile-status-info"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[260px]">
                      <p className="text-xs leading-relaxed">
                        Status is set automatically based on the animal&apos;s public MSPCA
                        adoption listing and checklist completion. It cannot be edited manually.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Last Scanned:</span>
                  <span
                    className="text-xs text-muted-foreground"
                    data-testid="text-last-scraped"
                  >
                    {lastCheckedLabel}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* 50/50 grid: photo left, bio right */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">

            {/* Photo column */}
            <div className="space-y-2">
              <p className="text-sm font-medium" data-testid="label-current-primary-photo">Current Primary Photo</p>
              {isLoading ? (
                <Skeleton className="aspect-video w-full rounded-md" />
              ) : scrapedPhotoUrl ? (
                <div className="overflow-hidden rounded-md" data-testid="div-adoption-photo">
                  <img
                    src={scrapedPhotoUrl}
                    alt={`${animalName} — public adoption photo`}
                    className="w-full object-cover"
                    data-testid="img-adoption-photo"
                  />
                </div>
              ) : (
                <div
                  className="relative h-64 overflow-hidden rounded-md bg-muted"
                  data-testid="div-adoption-photo-empty"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <AnimalAvatar
                      animalId={animalId}
                      photoUrl={animalData?.photoUrl ?? null}
                      name={animalName}
                      species={animalSpecies}
                      className="h-56 w-56"
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center px-4">
                    <div className="rounded-md bg-background/80 px-3 py-2 text-center backdrop-blur-sm max-w-[90%]">
                      {profileData?.profileStatus === "unpublished" ? (
                        <p className="text-xs text-muted-foreground" data-testid="text-photo-unpublished">
                          This profile is currently unpublished.<br />Once published, {possessiveName} current primary photo will appear here.
                        </p>
                      ) : !profileData?.lastScrapedAt ? (
                        <p className="text-xs text-muted-foreground" data-testid="text-not-scraped">
                          If a photo and bio are available on the MSPCA website, they will appear here.
                        </p>
                      ) : !profileData.profileFound ? (
                        <p className="text-xs text-muted-foreground" data-testid="text-listing-not-found">
                          No public adoption listing found on mspca.org.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground" data-testid="text-photo-not-found">
                          No photo found on the public listing.<br />Photos are pulled from {possessiveName} live profile on mspca.org.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bio column */}
            <div className="space-y-2">
              <p className="text-sm font-medium" data-testid="label-current-live-bio">Current Live Bio</p>
              <div>
              {isLoading ? (
                <div className="space-y-2" data-testid="div-bio-loading">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-4/6" />
                  <Skeleton className="h-3 w-5/6" />
                </div>
              ) : profileData?.bioText ? (
                <>
                  <div
                    ref={bioContentRef}
                    className={`text-sm space-y-3 transition-all ${bioExpanded ? "" : "max-h-64 overflow-hidden"}`}
                    data-testid="text-bio-content"
                  >
                    {profileData.bioText.split("\n\n").map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                  {(bioOverflows || bioExpanded) && (
                    <button
                      type="button"
                      onClick={() => setBioExpanded((prev) => !prev)}
                      className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                      data-testid="button-bio-toggle"
                    >
                      {bioExpanded ? "Show less" : "Show more"}
                    </button>
                  )}
                </>
              ) : (
                <div
                  className="flex h-64 items-center justify-center rounded-md bg-muted px-6 text-center"
                  data-testid="div-bio-placeholder"
                >
                  <p className="text-sm text-muted-foreground">
                    {profileData?.profileStatus === "unpublished" ? (
                      <>This profile is currently unpublished.<br />Once published, {possessiveName} bio will appear here.</>
                    ) : !profileData?.lastScrapedAt ? (
                      <>If a photo and bio are available on the MSPCA website, they will appear here.</>
                    ) : profileData?.profileFound ? (
                      <>No bio found on the public listing.<br />Bio text is pulled from {possessiveName} live profile on mspca.org.</>
                    ) : (
                      <>Bio will appear here once available.</>
                    )}
                  </p>
                </div>
              )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Custom Avatar */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium">Custom Avatar</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="About custom avatar"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[280px]">
                  <p className="text-xs leading-relaxed">
                    BETI uses {possessiveName} public adoption photo as their default avatar across the app. Upload a custom avatar to use instead. Custom avatars are only used within BETI and are not added to Chameleon or mspca.org.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handlePhotoFileChange}
              data-testid="input-animal-photo"
            />
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => photoInputRef.current?.click()}
                disabled={photoUploadMutation.isPending}
                data-testid="button-upload-photo"
              >
                <Camera className="h-4 w-4 mr-1.5" />
                {animalData?.photoUrl ? "Change photo" : "Upload photo"}
              </Button>
              {animalData?.photoUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => photoRemoveMutation.mutate()}
                  disabled={photoRemoveMutation.isPending}
                  data-testid="button-remove-photo"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Remove photo
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={notesModalOpen} onOpenChange={setNotesModalOpen}>
        <DialogContent className="max-w-3xl w-full h-[85vh] flex flex-col" data-testid="dialog-notes-fullsize">
          <DialogHeader>
            <DialogTitle>Adoption Profile Notes — {animalName}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 pt-2">
            <EntityNotesSection
              entityType="animal_profile"
              entityId={animalId}
              canEdit={!!currentUser}
              currentUserId={currentUser?.id}
              hideLabel
              shareLabel={`${animalName} (${animalId}) — Profile Notes`}
            />
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
